import { useMemo, useState, useCallback, useContext, useEffect } from 'react'
import { max, min, nonNullable } from 'lib/array/arrayUtils'
import { countTravellersAsOccupants, countOccupants } from 'lib/offer/occupancyUtils'
import { isLEHotel } from 'lib/offer/offerTypes'
import useInsuranceQuote from 'hooks/Insurance/useInsuranceQuote'
import useInsuranceProducts from 'hooks/Insurance/useInsuranceProducts'
import GeoContext from 'contexts/geoContext'
import useInsuranceUpsellCommonData from './useInsuranceUpsellCommonData'
import {
  get as getLocalStorage,
  set as setLocalStorage,
} from 'lib/storage/isomorphicLocalStorage'
import { getUpsellDismissalStorageKey } from 'lib/order/upsellUtils'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import getFlightDetailsByOrderItems from 'lib/flights/getFlightDetailsByOrderItems'
import { isSameDay } from 'lib/datetime/dateUtils'
import moment from 'moment'
import { ISO_DATE_FORMAT } from 'constants/dateFormats'
import { findByTwoLetterCode } from 'lib/insurance/insuranceCountries'
import { fetchBookingDetailsByIds } from 'actions/ExperienceActions'
import getInsuranceCoverAmountFromOrder from 'lib/insurance/getInsuranceCoverAmountFromOrder'
import useCheapestInsuranceQuotePrice from './useCheapestInsuranceQuotePrice'
import { generateInsuranceItem } from 'lib/checkout/insurance/cart'
import { initialiseCheckout } from 'actions/CheckoutActions'
import { navigateToCheckout } from 'checkout/actions/navigateActions'

interface InsuranceUpsellData {
  offerId?: string;
  occupants?: App.Occupants;
  destinationCountries?: Array<string>;
  destinationCountryCode?: string | null;
  provider?: string;
  reservationType?: string;
  startDate: string;
  endDate: string;
  status?: App.OrderItemStatus;
  type: string;
}

const localStorageKey = 'insurance-booking-protection'

function useInsuranceBookingProtectionUpsell(
  order: App.Order,
): {
  insuranceBookingProtectionUpsellEnabled: true
  insuranceBookingProtectionUpsell: App.InsuranceUpsell
  handleInsuranceBookingProtectionUpsellDismiss: () => void
  handleInsuranceBookingProtectionSelect: () => void
  insuranceBookingProtectionDataLoading: boolean
} | {
  insuranceBookingProtectionUpsellEnabled: false
  insuranceBookingProtectionUpsell: undefined
  handleInsuranceBookingProtectionUpsellDismiss: undefined
  handleInsuranceBookingProtectionSelect: undefined
  insuranceBookingProtectionDataLoading: boolean
} {
  const dispatch = useAppDispatch()

  const [dismissed, setDismissed] = useState<boolean>(!!getLocalStorage(getUpsellDismissalStorageKey(localStorageKey, order.id)))

  const {
    luxplus: { fetchingLuxPlusSubscription, ableToViewLuxPlusBenefits, luxPlusDiscountedInsuranceEnabled },
    offers: { offers, fetching },
    flags: { orderHasInsurance, isInsuranceBookingProtectionSupported },
  } = useInsuranceUpsellCommonData(order)

  useEffect(() => {
    const experienceItems = order.experienceItems
    const ids = experienceItems.map(item => item.id)
    if (experienceItems.length) {
      dispatch(fetchBookingDetailsByIds(ids))
    }
  }, [dispatch, order.experienceItems])

  const handleInsuranceBookingProtectionUpsellDismiss = useCallback(() => {
    setLocalStorage(getUpsellDismissalStorageKey(localStorageKey, order.id), true)
    setDismissed(true)
  }, [order.id])

  const flightDetails = useAppSelector(state => state.orders.flightDetails)
  const experienceOffers = useAppSelector(state => state.experience.experiences)
  const experienceBookingDetails = useAppSelector(state => state.experience.bookingDetails)

  const [earliestItem, latestItem] = useMemo(() => {
    const possibleUpsells: Array<InsuranceUpsellData> = [
      ...nonNullable(order.items.map(item => {
        const hotelOffer = offers.find(offer => offer.id === item.offer.id)
        if (isLEHotel(hotelOffer) && item.offer.property && item.reservation) {
          return {
            offerId: item.offer.id,
            occupants: {
              adults: item.reservation.adults,
              children: item.reservation.children,
              infants: item.reservation.infants,
            },
            destinationCountries: nonNullable([hotelOffer.property.geoData.country]),
            destinationCountryCode: hotelOffer.property.geoData.countryCode,
            reservationType: item.reservationType,
            startDate: item.reservation.startDate,
            endDate: item.reservation.endDate,
            status: item.status,
            type: 'hotel',
          }
        }
      })),
      ...nonNullable(order.bedbankItems.map(item => {
        const occupancies: Array<App.Occupants> = item.rooms.map(room => room.occupancy)
        const occupants = countOccupants(occupancies)

        return {
          offerId: item.offer.id,
          occupants: {
            adults: occupants?.adults,
            children: occupants?.children,
            infants: occupants?.infants,
          },
          destinationCountries: [item.offer.property.address.countryName],
          reservationType: item?.reservationId,
          startDate: item.checkIn.format('YYYY-MM-DD'),
          endDate: item.checkOut.format('YYYY-MM-DD'),
          status: item.status,
          type: 'bedbank_hotel',
        }
      })),
      ...nonNullable(order.flightItems.map(item => {
        // Flight details
        const flightDetailsByOrderItems = getFlightDetailsByOrderItems(order, flightDetails)
        if (!flightDetailsByOrderItems) return

        // Occupants
        const flightItemsIds = order.flightItems.map(item => item.id)
        const travellers = flightItemsIds.reduce<Array<App.FlightTraveller>>((acc, curr) => {
          const currentFlightDetails: App.FlightOrderItemDetails | undefined = flightDetailsByOrderItems[curr]
          const travellers = currentFlightDetails?.travellers || []
          return [...acc, ...travellers]
        }, [])
        const occupants = countTravellersAsOccupants(travellers)

        // Dates
        const startDate = item.departureDate
        let endDate = item.arrivalDate
        if (!startDate || !endDate) return
        /**
         * In one-way standalone flights, start date and end date are the same
         * We need to add 1 day to the end date, otherwise the insurance quote payload isn't valid
         */
        if (isSameDay(new Date(startDate), new Date(endDate))) {
          endDate = moment(endDate).add(1, 'day').format(ISO_DATE_FORMAT)
        }

        // Destination countries
        const destinationCountries = flightItemsIds.reduce((acc, curr) => {
          const currentFlightDetails: App.FlightOrderItemDetails | undefined = flightDetailsByOrderItems[curr]
          const countryCodes = currentFlightDetails?.bookingJourney.legs.map(leg => leg.arrivalCountry) || []
          return [...acc, ...countryCodes]
        }, []).filter(Boolean)

        return {
          offerId: item.reservationId,
          occupants: {
            adults: occupants?.adults,
            children: occupants?.children,
            infants: occupants?.infants,
          },
          destinationCountries,
          reservationType: item.reservationId,
          startDate,
          endDate,
          status: item.status,
          type: 'flight',
        }
      })),
      ...nonNullable(order.experienceItems.map(item => {
        const experienceId = item.experienceId

        // Occupants
        const totalTickets = Object.values(experienceBookingDetails || {}).filter(booking => booking.orderId === order.id).length
        const occupants = {
          adults: totalTickets,
          children: 0,
          infants: 0,
        }

        // Dates
        const startDate = item.date ?? item.bookByDate!
        const endDate = moment(startDate).add(1, 'day').format(ISO_DATE_FORMAT)

        // Destination countries
        const offer = experienceOffers[experienceId]
        const destinationCountries = offer?.redemptionLocations?.map(location => findByTwoLetterCode(location.countryCode)) || []

        return {
          offerId: item.experienceId,
          occupants,
          destinationCountries: nonNullable(destinationCountries),
          reservationType: item.bookingType,
          startDate,
          endDate,
          status: item.status,
          type: 'experience',
        }
      })),
    ].filter(item => item.status === 'completed')

    const earliest = min(possibleUpsells, item => new Date(item.startDate))
    const latest = max(possibleUpsells, item => new Date(item.endDate))

    return [earliest, latest]
  }, [order, offers, flightDetails, experienceBookingDetails, experienceOffers])

  const { currentRegionName } = useContext(GeoContext)
  const isDomestic = useMemo(() => !!earliestItem?.destinationCountries?.every(country => country == currentRegionName), [currentRegionName, earliestItem?.destinationCountries])
  const [products] = useInsuranceProducts('booking-protection', {
    isDomestic,
  })
  const [quote, fetchingQuotes] = useInsuranceQuote(products[0]?.id,
    {
      startDate: earliestItem?.startDate,
      endDate: latestItem?.endDate,
      destinationCountries: earliestItem?.destinationCountries,
      coverAmount: getInsuranceCoverAmountFromOrder(order),
      travellers: earliestItem?.occupants,
      type: 'booking-protection',
    })

  const [cheapestQuotePrice, cheapestQuoteLuxPlusPrice] = useCheapestInsuranceQuotePrice(
    { ableToViewLuxPlusBenefits, luxPlusDiscountedInsuranceEnabled, quote, earliestItem, latestItem },
  )

  const handleInsuranceBookingProtectionSelect = useCallback(() => {
    const product = products.length ? products[0] : undefined
    if (!product) return

    const newItem = generateInsuranceItem({ product })
    dispatch(initialiseCheckout([newItem], {
      regionCode: order.regionCode,
      currencyCode: order.currencyCode,
      mode: 'insurance',
      order,
    }))
    dispatch(navigateToCheckout())
  }, [dispatch, order, products])

  const enabled = !dismissed &&
    !orderHasInsurance &&
    isInsuranceBookingProtectionSupported

  const insuranceBookingProtectionUpsell = useMemo<App.InsuranceUpsell>(() => {
    return {
      checkCanViewLuxPlusBenefits: ableToViewLuxPlusBenefits,
      isEnabledLuxPlusDiscountedInsurance: luxPlusDiscountedInsuranceEnabled,
      fetchingLuxPlusSubscription,
      fetchingQuotes,
      cheapestQuotePrice,
      cheapestQuoteLuxPlusPrice,
      orderId: order.id,
    }
  }, [
    ableToViewLuxPlusBenefits,
    cheapestQuoteLuxPlusPrice,
    cheapestQuotePrice,
    fetchingLuxPlusSubscription,
    fetchingQuotes,
    luxPlusDiscountedInsuranceEnabled,
    order.id,
  ])

  if (enabled) {
    return {
      insuranceBookingProtectionUpsellEnabled: true,
      insuranceBookingProtectionUpsell,
      handleInsuranceBookingProtectionUpsellDismiss,
      handleInsuranceBookingProtectionSelect,
      insuranceBookingProtectionDataLoading: fetching,
    }
  } else {
    return {
      insuranceBookingProtectionUpsellEnabled: false,
      insuranceBookingProtectionUpsell: undefined,
      handleInsuranceBookingProtectionUpsellDismiss: undefined,
      handleInsuranceBookingProtectionSelect: undefined,
      insuranceBookingProtectionDataLoading: fetching,
    }
  }
}

export default useInsuranceBookingProtectionUpsell
